import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, Pagination, Row, Col, Card, Empty } from 'antd';
import { useGetPublicNews } from '../../api/hooks/useNewsQuery';
import { NewsItem as NewsItemType } from '../../api/services/newsService';
import { Image } from 'antd';

const NewsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const { data: newsResponse, isLoading, error } = useGetPublicNews(currentPage, pageSize);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNewsClick = (newsId: number) => {
    navigate(`/news/${newsId}`);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-red-600">{t('errorLoadingNews')}</h1>
        </div>
      </div>
    );
  }

  const newsItems = newsResponse?.data || [];
  const total = newsResponse?.total || 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="mb-4 text-center text-4xl font-bold">{t('NewsTitle')}</h1>
        <p className="text-center text-lg text-gray-600">{t('newsSubtitle')}</p>
      </div>

      {newsItems.length === 0 ? (
        <Empty description={t('noNewsAvailable')} />
      ) : (
        <>
          <Row gutter={[24, 24]} className="mb-8">
            {newsItems.map((news: NewsItemType) => (
              <Col xs={24} sm={12} lg={8} key={news.id}>
                <Card
                  hoverable
                  className="h-full shadow-lg transition-shadow duration-300"
                  cover={
                    news.preview ? (
                      <Image src={news.preview} alt={news.title} className="h-48 object-cover" />
                    ) : (
                      <div className="flex h-48 items-center justify-center bg-gray-200">
                        <span className="text-gray-500">{t('noImage')}</span>
                      </div>
                    )
                  }
                  onClick={() => handleNewsClick(news.id)}
                >
                  <Card.Meta
                    title={<h3 className="line-clamp-2 text-lg font-semibold">{news.title}</h3>}
                    description={
                      <div>
                        <p className="mb-3 line-clamp-3 text-gray-600">{news.summary}</p>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>{new Date(news.createTime).toLocaleDateString()}</span>
                          <span className="cursor-pointer text-primary hover:underline">
                            {t('NewsItemReadMore')}
                          </span>
                        </div>
                      </div>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>

          {total > pageSize && (
            <div className="flex justify-center">
              <Pagination
                current={currentPage}
                total={total}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NewsList;
